import { FormEvent, useMemo } from 'react';
import { Loader2 } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import ToggleSwitchCard from '../../../components/Forms/Input/ToggleSwitchCard';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';

import { RestaurantDiningRoomDTO } from '../../../types/restaurant/RestaurantDiningRoomTypes';
import { RestaurantTableForm } from '../../../types/restaurant/RestaurantTableTypes';

interface Props {
  isOpen: boolean;
  form: RestaurantTableForm;
  diningRooms: RestaurantDiningRoomDTO[];
  saving: boolean;
  onClose: () => void;
  onChange: <K extends keyof RestaurantTableForm>(
    field: K,
    value: RestaurantTableForm[K]
  ) => void;
  onSave: () => void;
}

const TableFormModal = ({
  isOpen,
  form,
  diningRooms,
  saving,
  onClose,
  onChange,
  onSave,
}: Props) => {
  const errors = useMemo(() => {
    const next: Record<string, string> = {};

    if (!form.name.trim()) next.name = 'El nombre es obligatorio.';
    else if (form.name.trim().length > 100)
      next.name = 'Máximo 100 caracteres.';

    if (form.capacity < 1 || form.capacity > 1000)
      next.capacity = 'La capacidad debe estar entre 1 y 1000.';

    if (form.displayOrder < 0)
      next.displayOrder = 'El orden no puede ser negativo.';

    return next;
  }, [form]);

  const hasErrors = !!Object.keys(errors).length;

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!hasErrors) {
      onSave();
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={form.id ? 'Editar mesa' : 'Nueva mesa'}
      maxWidth="max-w-3xl"
      headerExtra={<RestaurantFormHelp topic="table" />}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
          <SelectField
            label="Salón"
            name="diningRoomId"
            value={form.diningRoomId}
            iconKey="store"
            placeholder="Seleccione una opción"
            options={[
              { value: 0, label: 'Sin salón asignado' },
              ...diningRooms.map((room) => ({
                value: room.id,
                label: room.name,
              })),
            ]}
            onChange={(event) =>
              onChange(
                'diningRoomId',
                Number(event.target.value) === 0
                  ? ''
                  : Number(event.target.value)
              )
            }
          />

          {form.id && (
            <InputField
              label="Código"
              name="code"
              type="text"
              iconKey="code"
              value={form.code ?? ''}
              disabled
              onChange={() => undefined}
            />
          )}

          <InputField
            label="Nombre"
            name="name"
            type="text"
            iconKey="table"
            value={form.name}
            required
            error={errors.name}
            placeholder="Ej. Mesa 1"
            onChange={(event) => onChange('name', event.target.value)}
          />

          <InputField
            label="Capacidad"
            name="capacity"
            type="number"
            iconKey="user"
            value={form.capacity}
            min={1}
            max={1000}
            required
            error={errors.capacity}
            onChange={(event) =>
              onChange('capacity', Number(event.target.value))
            }
          />

          <SelectField
            label="Forma"
            name="shape"
            value={form.shape}
            iconKey="layout"
            options={[
              { value: 'SQUARE', label: 'Cuadrada' },
              { value: 'RECTANGLE', label: 'Rectangular' },
              { value: 'ROUND', label: 'Redonda' },
            ]}
            onChange={(event) =>
              onChange(
                'shape',
                event.target.value as RestaurantTableForm['shape']
              )
            }
          />

          <SelectField
            label="Estado inicial"
            name="status"
            value={form.status}
            iconKey="status"
            options={[
              { value: 'FREE', label: 'Libre' },
              { value: 'OCCUPIED', label: 'Ocupada' },
              { value: 'RESERVED', label: 'Reservada' },
              { value: 'CLEANING', label: 'En limpieza' },
              { value: 'OUT_OF_SERVICE', label: 'Fuera de servicio' },
            ]}
            onChange={(event) =>
              onChange(
                'status',
                event.target.value as RestaurantTableForm['status']
              )
            }
          />

          <InputField
            label="Posición X (opcional)"
            name="positionX"
            type="number"
            iconKey="layout"
            value={form.positionX}
            step="0.01"
            onChange={(event) =>
              onChange(
                'positionX',
                event.target.value ? Number(event.target.value) : ''
              )
            }
          />

          <InputField
            label="Posición Y (opcional)"
            name="positionY"
            type="number"
            iconKey="layout"
            value={form.positionY}
            step="0.01"
            onChange={(event) =>
              onChange(
                'positionY',
                event.target.value ? Number(event.target.value) : ''
              )
            }
          />

          <InputField
            label="Orden de visualización"
            name="displayOrder"
            type="number"
            iconKey="list"
            value={form.displayOrder}
            min={0}
            error={errors.displayOrder}
            onChange={(event) =>
              onChange('displayOrder', Math.max(0, Number(event.target.value)))
            }
          />

          {form.id && (
            <div className="mb-4">
              <ToggleSwitchCard
                label="Registro activo"
                name="tableActive"
                checked={form.active ?? true}
                onChange={() => onChange('active', !(form.active ?? true))}
                description="Permite habilitar o deshabilitar esta mesa sin eliminarla del sistema."
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-stroke pt-5 dark:border-strokedark">
          <div className="w-full sm:w-auto">
            <ActionButton
              label="Cancelar"
              variant="secondary"
              size="md"
              fullWidth={false}
              disabled={saving}
              onClick={onClose}
            />
          </div>

          <div className="w-full sm:w-auto">
            <button
              type="submit"
              disabled={saving || hasErrors}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Guardando...' : 'Guardar mesa'}
            </button>
          </div>
        </div>
      </form>
    </FormModal>
  );
};

export default TableFormModal;
